import { FormControlLabel, Typography } from "@mui/material";
import {
	CoreCheckbox,
	CoreModal,
	CoreRadioGroup,
	CoreSearchField,
	CoreSpinner,
	CoreTextBox,
	RadioOption,
} from "../../core";
import i18nPackage from "../../i18n/i18n";
import styles from "./SaveProfileModal.module.css";
import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { VerityApiContext } from "../../api/fetch";
import { useMobileLayout } from "../../hooks";
import { ProctoringOptionProfileType } from "@verity/database/prisma";
import type { Lms } from "@verity/api-client/types";
import WarningIcon from "../images/warning.svg?react";
import { Trans } from "react-i18next";
import { useEditQuizStyles } from "../components";
import { ResponsiveModalBody } from "../components/EditQuizModalComponents";
import { PROFILE_CONFIG } from "../edit-quiz-steps/StepIconConfig";
import { getSubAccountTypeName } from "../profile/profileHelpers";

export type PublishLocation = Exclude<ProctoringOptionProfileType, "assessment" | "preset">;

type SubAccountOption = { id: number; accountName: string; lmsAccountId: string };

const PUBLISH_LOCATION_LABEL_KEYS = {
	institute: "instituteDescription",
	subAccount: "subAccountDescription",
	course: "courseDescription",
	user: "userDescription",
} as const satisfies Record<PublishLocation, string>;

function ProfileRadioRow({ type, lms }: { type: PublishLocation; lms?: Lms }) {
	const labelKey = PUBLISH_LOCATION_LABEL_KEYS[type];
	const ProfileIcon = PROFILE_CONFIG[type].Icon;
	return (
		<div className={styles.profileRadioRow}>
			<div
				className={`${styles.iconWrapper} ${styles[type]}`}
				style={{ background: PROFILE_CONFIG[type].bgColor, color: PROFILE_CONFIG[type].svgColor }}
			>
				{<ProfileIcon />}
			</div>

			<Typography component={"span"} className={styles.description}>
				<Trans
					i18n={i18nPackage}
					i18nKey={`editQuizPage.saveProfileModalTSX.${labelKey}`}
					values={{ type: getSubAccountTypeName(lms), typePlural: getSubAccountTypeName(lms, { plural: true }) }}
					components={{ b: <strong /> }}
				/>
			</Typography>
		</div>
	);
}

export type SaveProfileModalProps = {
	onClose: () => void;
	onSave: (profileName: string, publishLocation: PublishLocation | undefined, subAccountIds?: number[]) => void;
	hasInstituteProfilePermissions: boolean;
	canPublishToCourse: boolean;
	isThirdPartyEnabled: boolean;
	lms?: Lms;
};

export default function SaveProfileModal({
	onClose,
	onSave,
	hasInstituteProfilePermissions,
	isThirdPartyEnabled,
	canPublishToCourse,
	lms,
}: SaveProfileModalProps) {
	const { getModalInModalProps } = useEditQuizStyles();
	const { getInstituteScopeSettings } = useContext(VerityApiContext);
	const [profileName, setProfileName] = useState<string>("");
	const [publishLocation, setPublishLocation] = useState<PublishLocation>("user");
	const [subAccounts, setSubAccounts] = useState<SubAccountOption[]>([]);
	const [subAccountsLoading, setSubAccountsLoading] = useState(false);
	const [selectedSubAccountIds, setSelectedSubAccountIds] = useState<number[]>([]);
	const [subAccountSearch, setSubAccountSearch] = useState("");
	const isMobileLayout = useMobileLayout();
	const radioGroupAriaId = useId();
	const subAccountListId = useId();
	const subAccountsRequestedRef = useRef(false);

	useEffect(() => {
		if (!hasInstituteProfilePermissions || publishLocation !== "subAccount" || subAccountsRequestedRef.current) {
			return;
		}

		let ignore = false;
		subAccountsRequestedRef.current = true;
		setSubAccountsLoading(true);
		void getInstituteScopeSettings()
			.then((scopeSettings) => {
				if (!ignore) setSubAccounts(scopeSettings.allAccounts);
			})
			.catch(() => {
				if (!ignore) setSubAccounts([]);
			})
			.finally(() => {
				if (!ignore) setSubAccountsLoading(false);
			});

		return () => {
			ignore = true;
		};
	}, [getInstituteScopeSettings, hasInstituteProfilePermissions, publishLocation]);

	const filteredSubAccounts = useMemo(() => {
		const search = subAccountSearch.trim().toLowerCase();
		if (!search) return subAccounts;
		return subAccounts.filter(
			(subAccount) =>
				subAccount.accountName.toLowerCase().includes(search) || subAccount.lmsAccountId.toLowerCase().includes(search),
		);
	}, [subAccountSearch, subAccounts]);

	const toggleSubAccount = useCallback((subAccountId: number, checked: boolean) => {
		setSelectedSubAccountIds((current) =>
			checked ? [...current, subAccountId] : current.filter((selectedId) => selectedId !== subAccountId),
		);
	}, []);

	const subAccountTypeNamePlural = getSubAccountTypeName(lms, { plural: true });
	const isSubAccountScope = publishLocation === "subAccount";
	const canSave = Boolean(profileName) && (!isSubAccountScope || selectedSubAccountIds.length > 0);

	const radioOptions: RadioOption[] = [
		...(hasInstituteProfilePermissions
			? [
					{
						value: "institute",
						label: <ProfileRadioRow type="institute" lms={lms} />,
					},
					// Sub-accounts are an LMS construct, so standalone institutions have none to publish to.
					...(lms
						? [
								{
									value: "subAccount",
									label: <ProfileRadioRow type="subAccount" lms={lms} />,
								},
							]
						: []),
				]
			: []),
		...(canPublishToCourse
			? [
					{
						value: "course",
						label: <ProfileRadioRow type="course" lms={lms} />,
					},
				]
			: []),

		{
			value: "user",
			label: <ProfileRadioRow type="user" lms={lms} />,
		},
	];

	const title = i18nPackage.t("editQuizPage.saveProfileModalTSX.title");

	return (
		<CoreModal
			{...getModalInModalProps(isMobileLayout, title)}
			open
			onClose={onClose}
			minWidth={isMobileLayout ? undefined : "min(80%, 760px)"}
			widthFixed
			padding={isMobileLayout ? undefined : "1rem 1.5rem 0 1.5rem"}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.saveProfileModalTSX.cancel"),
					color: "secondary",
					style: { marginLeft: isMobileLayout ? "auto" : undefined },
					onClick: onClose,
				},
				{
					text: i18nPackage.t("editQuizPage.saveProfileModalTSX.save"),
					color: "primary",
					disabled: !canSave,
					onClick: () => onSave(profileName, publishLocation, isSubAccountScope ? selectedSubAccountIds : undefined),
				},
			]}
		>
			<ResponsiveModalBody title={title} description={i18nPackage.t("editQuizPage.saveProfileModalTSX.description")}>
				<div className={styles.saveProfileContinaer}>
					<div className={styles.warningWrapper}>
						<div className={styles.warningIconContainer}>
							<WarningIcon />
						</div>
						<Typography>
							<Trans
								i18n={i18nPackage}
								i18nKey={`editQuizPage.saveProfileModalTSX.${isThirdPartyEnabled ? "warning" : "warningNoThirdParty"}`}
								components={{ b: <strong /> }}
							/>
						</Typography>
					</div>
					<div className={styles.section}>
						<Typography className={styles.title}>
							{i18nPackage.t("editQuizPage.saveProfileModalTSX.details")}
						</Typography>
						<Typography className={styles.description}>
							{i18nPackage.t("editQuizPage.saveProfileModalTSX.detailsDescription")}
						</Typography>
						<FormControlLabel
							label={i18nPackage.t("editQuizPage.saveProfileModalTSX.profileName")}
							control={
								<CoreTextBox
									value={profileName}
									autoComplete="off"
									inputProps={{ maxLength: 30 }}
									placeholder={i18nPackage.t("editQuizPage.saveProfileModalTSX.enterProfileName")}
									onChange={(event) => setProfileName(event.target.value)}
									className={styles.profileNameTextfield}
								/>
							}
							labelPlacement="start"
							className={styles.profileNameLabel}
						/>
					</div>
					<div className={styles.section}>
						<Typography className={styles.title}>
							{i18nPackage.t("editQuizPage.saveProfileModalTSX.saveLocation")}
						</Typography>
						<Typography className={styles.description} id={radioGroupAriaId}>
							{i18nPackage.t("editQuizPage.saveProfileModalTSX.saveLocationDescription")}
						</Typography>

						<CoreRadioGroup
							onChange={(_e, value) => setPublishLocation(value as typeof publishLocation)}
							radioGroupAriaLabelledby={radioGroupAriaId}
							radioOptions={radioOptions}
							selectedValue={publishLocation}
							className={styles.profileRadioGroup}
						/>

						{isSubAccountScope && (
							<div className={styles.subAccountSelector}>
								<CoreSearchField
									noForm
									label={i18nPackage.t("editQuizPage.saveProfileModalTSX.searchSubAccounts", {
										type: subAccountTypeNamePlural,
									})}
									onSearch={setSubAccountSearch}
									ariaControls={subAccountListId}
									totalResults={filteredSubAccounts.length}
									value={subAccountSearch}
									fullWidth
								/>
								<div
									className={styles.subAccountList}
									id={subAccountListId}
									role="group"
									aria-label={i18nPackage.t("editQuizPage.saveProfileModalTSX.selectSubAccounts", {
										type: subAccountTypeNamePlural,
									})}
								>
									{subAccountsLoading ? (
										<CoreSpinner size={24} />
									) : filteredSubAccounts.length === 0 ? (
										<Typography className={styles.subAccountEmpty}>
											{i18nPackage.t("editQuizPage.saveProfileModalTSX.noSubAccounts", {
												type: subAccountTypeNamePlural,
											})}
										</Typography>
									) : (
										filteredSubAccounts.map((subAccount) => (
											<label key={subAccount.id} className={styles.subAccountRow}>
												<CoreCheckbox
													checked={selectedSubAccountIds.includes(subAccount.id)}
													onChange={(event) => toggleSubAccount(subAccount.id, event.target.checked)}
													inputProps={{ "aria-label": subAccount.accountName }}
												/>
												<span className={styles.subAccountName}>
													{`${subAccount.accountName} (${subAccount.lmsAccountId})`}
												</span>
											</label>
										))
									)}
								</div>
							</div>
						)}
					</div>
				</div>
			</ResponsiveModalBody>
		</CoreModal>
	);
}
